import React, { useState } from "react";
import { useSelector } from "react-redux";
import { createPortal } from "react-dom";
import { DndContext, DragOverlay, PointerSensor, pointerWithin, useDraggable, useDroppable } from "@dnd-kit/core";
import { Empty, Progress, Table, Tag, Tooltip } from "antd";
import { EyeOutlined, ToolOutlined, WarningFilled, ClockCircleOutlined, FilePdfOutlined } from "@ant-design/icons";
import { ButtonItem } from "../../../../../Components/Items";
import { formatearTiempo, formatearFechaHora } from "./planeacion.utils";
import ModalAjustarInicioRealPU from "./ModalAjustarInicioRealPU";
import ModalPdfPlaneacionPU from "./ModalPdfPlaneacionPU";
import VistaOrdenDeProduccionPU from "../../VistaOrdenDeProduccionPU/VistaOrdenDeProduccionPU";

const TOLERANCIA_MIN = 5;

const SENSORES_PLANEACION = [{ sensor: PointerSensor, options: { activationConstraint: { distance: 8 } } }];

const idCelda = (ordenId, etapaId) => `${ordenId}:${etapaId}`;

const destinosDe = (etapa) => new Set((etapa?.transiciones || []).map((t) => (t.etapaDestinoId?._id || t.etapaDestinoId)?.toString()));

const useFormatoTiempo = () => {
  const unidad = useSelector((state) => state.empresa.unidadTiempoProduccion);
  return (minutos) => formatearTiempo(minutos, unidad);
};

const ordenarEtapas = (etapas = []) => {
  const peso = (e) => (e.esDefectuoso ? 2 : e.esFinal ? 1 : 0);
  return [...etapas].sort((a, b) => peso(a) - peso(b) || (a.orden || 0) - (b.orden || 0));
};

const TagRetraso = ({ minutos }) => {
  const formatearMinutos = useFormatoTiempo();
  if (minutos > TOLERANCIA_MIN) return <Tag color="red">Retraso {formatearMinutos(minutos)}</Tag>;
  if (minutos < -TOLERANCIA_MIN) return <Tag color="green">Adelanto {formatearMinutos(-minutos)}</Tag>;
  return <Tag color="green">A tiempo</Tag>;
};

const Dato = ({ label, children }) => (
  <div style={{ minWidth: 120 }}>
    <div style={{ fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px" }}>{label}</div>
    <div style={{ fontWeight: 500 }}>{children}</div>
  </div>
);

const Encabezado = ({ presupuesto, etiquetaRango, onReporte }) => {
  const { resumen = {} } = presupuesto;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>{presupuesto.nombre}</h3>
        <span style={{ color: "#8c8c8c", flex: 1 }}>
          {etiquetaRango} · {presupuesto.ordenes.length} de {presupuesto.totalOrdenes} órdenes en este rango
        </span>
        <ButtonItem rojo botonAncho icon={<FilePdfOutlined />} onClick={onReporte}>
          Reporte
        </ButtonItem>
      </div>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Dato label="Inicio planeado">{formatearFechaHora(resumen.inicioPlaneado)}</Dato>
        <Dato label="Inicio real">{resumen.inicioReal ? formatearFechaHora(resumen.inicioReal) : <span style={{ color: "#bfbfbf" }}>Sin iniciar</span>}</Dato>
        <Dato label="Fin planeado">{formatearFechaHora(resumen.finPlaneado)}</Dato>
        <Dato label="Fin proyectado">{formatearFechaHora(resumen.finProyectado)}</Dato>
        <Dato label="Estado">
          <TagRetraso minutos={resumen.retrasoMin || 0} />
        </Dato>
      </div>
    </div>
  );
};

const excedioEstimado = (dato) =>
  dato.minutosTrabajados !== null && dato.minutosEstimadosMovidos > 0 && dato.minutosTrabajados > dato.minutosEstimadosMovidos + TOLERANCIA_MIN;

const TooltipFaltante = ({ faltantes }) => (
  <div style={{ fontSize: 12 }}>
    <div style={{ fontWeight: 600, marginBottom: 4 }}>Falta material en el proyecto de producción</div>
    {faltantes.map((f) => (
      <div key={f.insumoId}>
        {f.clave ? f.clave + " - " : ""}
        {f.descripcion}: necesita {f.necesario}, hay {f.disponible}
      </div>
    ))}
  </div>
);

const CeldaEtapa = ({ etapa, dato, faltantes = [], enviados = [], ordenId, arrastreHabilitado, estadoSoltar, hayArrastre }) => {
  const formatearMinutos = useFormatoTiempo();
  const esTerminal = etapa.esFinal || etapa.esDefectuoso;
  const vacia = !dato || dato.piezas === 0;
  const piezasEnviadas = enviados.reduce((a, e) => a + (e.piezas || 0), 0);
  const soloEnviadas = !vacia && piezasEnviadas >= dato.piezas;
  const arrastrable = !!arrastreHabilitado && !vacia && !soloEnviadas && !etapa.esFinal && destinosDe(etapa).size > 0;
  const { setNodeRef: refSoltar } = useDroppable({ id: idCelda(ordenId, etapa._id), disabled: estadoSoltar !== "valida" && estadoSoltar !== "encima" });
  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `tarjeta-${idCelda(ordenId, etapa._id)}`,
    data: { ordenId, etapaId: etapa._id, piezas: dato?.piezas, color: etapa.color },
    disabled: !arrastrable,
  });
  if (!dato) return null;
  const excedido = excedioEstimado(dato);
  const color = etapa.color || "#1890ff";
  return (
    <div
      ref={refSoltar}
      style={{
        position: "relative",
        borderRadius: 8,
        padding: 2,
        outline: estadoSoltar === "encima" ? `2px dashed ${color}` : estadoSoltar === "valida" ? `2px dashed ${color}80` : "none",
        background: estadoSoltar === "encima" ? `${color}1f` : undefined,
        opacity: estadoSoltar === "invalida" ? 0.45 : 1,
        transition: "opacity 0.15s, background-color 0.15s",
      }}
    >
      <>
        <div
          ref={arrastrable ? setNodeRef : undefined}
          {...(arrastrable ? { ...attributes, ...listeners } : {})}
          onDragStart={arrastrable ? (e) => e.preventDefault() : undefined}
          style={{
            opacity: isDragging ? 0.4 : 1,
            userSelect: arrastrable ? "none" : undefined,
            touchAction: arrastrable ? "none" : undefined,
            borderLeft: `3px solid ${vacia ? "#f0f0f0" : etapa.color || "#1890ff"}`,
            borderRadius: 6,
            background: vacia ? "#fafafa" : "white",
            padding: "4px 8px",
            width: 100,
            height: 68,
            boxSizing: "border-box",
            overflow: "hidden",
            boxShadow: vacia ? "none" : "0 1px 3px rgba(0,0,0,0.08)",
            cursor: arrastrable ? "grab" : "default",
          }}
        >
          <div style={{ opacity: soloEnviadas ? 0.45 : 1 }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: vacia ? "#bfbfbf" : "#1a3a4a" }}>
            {dato.piezas} <span style={{ fontSize: 11, fontWeight: 400 }}>pzs</span>
          </div>
          {(dato.porcentaje > 0 || !vacia) && (
            <>
              <Progress percent={dato.porcentaje || 0} size="small" showInfo={false} strokeColor={color} style={{ margin: 0, lineHeight: 1 }} />
              <div style={{ fontSize: 11, color: "#595959", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {dato.porcentaje || 0}%
                {piezasEnviadas > 0 && (
                  <Tooltip title={enviados.map((e) => `${e.piezas} pzs enviadas a ${e.folio || "orden de retrabajo"}`).join(" · ")}>
                    <span style={{ color: "#722ed1", fontWeight: 600 }}> · → {enviados.map((e) => e.folio).filter(Boolean).join(", ")}</span>
                  </Tooltip>
                )}
                {!esTerminal && dato.minutosRestantes > 0 && !vacia ? ` · faltan ${formatearMinutos(dato.minutosRestantes)}` : ""}
              </div>
            </>
          )}
          {excedido && <div style={{ fontSize: 10, color: "#ff4d4f" }}>real &gt; estimado</div>}
          </div>
        </div>
      </>
      {faltantes.length > 0 && (
        <Tooltip title={<TooltipFaltante faltantes={faltantes} />} placement="bottom" overlayStyle={{ maxWidth: 380 }}>
          <WarningFilled style={{ position: "absolute", top: 4, right: 4, fontSize: 11, color: "#faad14", cursor: "help" }} />
        </Tooltip>
      )}
    </div>
  );
};

const PlaneacionPresupuestoPU = ({ presupuesto, presupuestoFiltroId, etiquetaRango, rango, onVer, onVerEtapas, onRefrescar, permisoMoverPiezas, permisoEditar }) => {
  const formatearMinutos = useFormatoTiempo();
  const [ordenAjuste, setOrdenAjuste] = useState(null);
  const [reporteVisible, setReporteVisible] = useState(false);
  const [arrastre, setArrastre] = useState(null);
  const [celdaEncima, setCeldaEncima] = useState(null);
  const [movimiento, setMovimiento] = useState(null);
  const etapas = ordenarEtapas(presupuesto.etapas);

  const estadoSoltarDe = (ordenId, etapaId) => {
    if (!arrastre) return null;
    if (arrastre.ordenId !== ordenId) return "invalida";
    if (arrastre.etapaId === etapaId) return "origen";
    const origen = etapas.find((e) => e._id === arrastre.etapaId);
    if (!destinosDe(origen).has(etapaId?.toString())) return "invalida";
    return celdaEncima === idCelda(ordenId, etapaId) ? "encima" : "valida";
  };

  const terminarArrastre = ({ over }) => {
    const origen = arrastre;
    setArrastre(null);
    setCeldaEncima(null);
    if (!origen || !over) return;
    const [ordenId, etapaDestinoId] = over.id.split(":");
    if (ordenId !== origen.ordenId || !destinosDe(etapas.find((e) => e._id === origen.etapaId)).has(etapaDestinoId)) return;
    setMovimiento({ ordenId, etapaOrigenId: origen.etapaId, etapaDestinoId, clave: Date.now() });
  };

  const columns = [
    {
      title: "Orden",
      key: "orden",
      fixed: "left",
      width: 190,
      render: (_, orden) => (
        <div>
          <div style={{ fontWeight: 600 }}>
            {orden.folio}
            {orden.terminada && <Tag color="green" style={{ marginLeft: 4 }}>Terminada</Tag>}
            {!orden.terminada && orden.faltaInventario && <Tag color="volcano" style={{ marginLeft: 4 }}>Falta inv.</Tag>}
          </div>
          <div style={{ fontSize: 13, fontWeight: 500, color: "#1a3a4a" }}>
            {orden.partidaSnapshot?.nombre || (orden.matricesResumen || []).map((m) => m.descripcion || m.clave).join(", ") || "—"}
          </div>
          {orden.esRetrabajo && (orden.retrabajoOrigen || []).length > 0 && (
            <div style={{ fontSize: 11, color: "#722ed1", fontWeight: 600 }}>
              De: {orden.retrabajoOrigen.map((o) => `${o.folio || "OP"} (${o.piezas} pzs)`).join(" · ")}
            </div>
          )}
          {orden.codigoProduccion && <div style={{ fontSize: 12, color: "#595959" }}>Código: {orden.codigoProduccion}</div>}
          {orden.presupuesto?.nombre && <div style={{ fontSize: 11, color: "#8c8c8c" }}>{orden.presupuesto.nombre}</div>}
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>{orden.cantidadEsperada} pzs · {formatearMinutos(orden.duracionEstimadaMin)}</div>
        </div>
      ),
    },
    {
      title: "Horario",
      key: "horario",
      width: 200,
      render: (_, orden) => {
        const atrasada = orden.retrasoMin > TOLERANCIA_MIN;
        return (
          <div style={{ fontSize: 12 }}>
            <div>
              <span style={{ color: "#8c8c8c" }}>Plan: </span>
              {formatearFechaHora(orden.planeado?.inicio)} → {formatearFechaHora(orden.planeado?.fin)}
            </div>
            <div style={{ color: atrasada ? "#ff4d4f" : "#389e0d" }}>
              <span style={{ color: "#8c8c8c" }}>{orden.terminada || orden.avance?.inicioReal ? "Real: " : "Proy.: "}</span>
              {formatearFechaHora(orden.proyectado?.inicio)} → {formatearFechaHora(orden.proyectado?.fin)}
              {orden.avance?.inicioAjustado && (
                <Tooltip
                  title={`Inicio ajustado por ${orden.avance.inicioAjustado.usuarioNombre || "—"}${orden.avance.inicioAjustado.motivo ? ": " + orden.avance.inicioAjustado.motivo : ""} (automático: ${formatearFechaHora(orden.avance.inicioRealAutomatico)})`}
                >
                  <Tag color="blue" style={{ marginLeft: 4, fontSize: 10, lineHeight: "16px", padding: "0 4px" }}>
                    ajustado
                  </Tag>
                </Tooltip>
              )}
              {permisoEditar && !orden.terminada && orden.avance?.inicioReal && (
                <Tooltip title="Ajustar inicio real">
                  <ClockCircleOutlined style={{ marginLeft: 6, color: "#00BFD6", cursor: "pointer" }} onClick={() => setOrdenAjuste(orden)} />
                </Tooltip>
              )}
            </div>
            {!(orden.duracionEstimadaMin > 0) && <div style={{ color: "#faad14" }}>Sin tiempo ciclo en sus matrices</div>}
          </div>
        );
      },
    },
    ...etapas.map((etapa) => ({
      title: <span style={{ color: etapa.esDefectuoso ? "#8c8c8c" : undefined }}>{etapa.nombre}</span>,
      key: etapa._id,
      width: 120,
      align: "center",
      render: (_, orden) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <CeldaEtapa
            etapa={etapa}
            dato={(orden.avance?.etapas || []).find((d) => d.etapaId === etapa._id)}
            faltantes={orden.faltanteMaterialPorEtapa?.[etapa._id]}
            enviados={etapa.esRetrabajo ? (orden.enviosARetrabajo || []).filter((e) => String(e.etapaId) === etapa._id) : []}
            ordenId={orden._id}
            arrastreHabilitado={permisoMoverPiezas && !orden.terminada && orden.estado === 1}
            estadoSoltar={estadoSoltarDe(orden._id, etapa._id)}
            hayArrastre={!!arrastre}
          />
        </div>
      ),
    })),
    {
      title: "",
      key: "acciones",
      fixed: "right",
      width: 80,
      render: (_, orden) => (
        <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
          <Tooltip title="Ver">
            <ButtonItem size="small" cyan icon={<EyeOutlined />} onClick={() => onVer(orden._id)} />
          </Tooltip>
          {permisoMoverPiezas && (
            <Tooltip title="Llenar (ver etapas / mover piezas)">
              <ButtonItem size="small" verde icon={<ToolOutlined />} onClick={() => onVerEtapas(orden._id)} />
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <Encabezado presupuesto={presupuesto} etiquetaRango={etiquetaRango} onReporte={() => setReporteVisible(true)} />
      {presupuesto.ordenes.length === 0 ? (
        <Empty description="No hay órdenes planeadas en este rango" />
      ) : (
        <DndContext
          sensors={SENSORES_PLANEACION}
          collisionDetection={pointerWithin}
          onDragStart={({ active }) => setArrastre(active.data.current || null)}
          onDragOver={({ over }) => setCeldaEncima(over?.id || null)}
          onDragEnd={terminarArrastre}
          onDragCancel={() => {
            setArrastre(null);
            setCeldaEncima(null);
          }}
        >
          <Table
            size="small"
            rowKey="_id"
            pagination={false}
            dataSource={presupuesto.ordenes}
            columns={columns}
            scroll={{ x: 190 + 200 + etapas.length * 120 + 80 }}
            onRow={(orden) => ({ onDoubleClick: () => onVerEtapas(orden._id) })}
          />
          {createPortal(
            <DragOverlay dropAnimation={null}>
              {arrastre ? (
                <div
                  style={{
                    borderLeft: `3px solid ${arrastre.color || "#1890ff"}`,
                    borderRadius: 6,
                    background: "white",
                    padding: "4px 8px",
                    minWidth: 80,
                    boxShadow: "0 8px 20px rgba(0,0,0,0.18)",
                    cursor: "grabbing",
                  }}
                >
                  <div style={{ fontWeight: 700, fontSize: 15, color: "#1a3a4a" }}>
                    {arrastre.piezas} <span style={{ fontSize: 11, fontWeight: 400 }}>pzs</span>
                  </div>
                </div>
              ) : null}
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      )}
      {movimiento && (
        <VistaOrdenDeProduccionPU
          key={movimiento.clave}
          soloMover
          ordenIdEmbebido={movimiento.ordenId}
          moverInicial={{ etapaOrigenId: movimiento.etapaOrigenId, etapaDestinoId: movimiento.etapaDestinoId }}
          onMovido={() => onRefrescar && onRefrescar()}
          onCerrarEmbebido={() => setMovimiento(null)}
        />
      )}
      <ModalPdfPlaneacionPU
        visible={reporteVisible}
        flujoId={presupuesto._id}
        flujoNombre={presupuesto.nombre}
        presupuestoPUId={presupuestoFiltroId}
        desde={rango?.desde}
        hasta={rango?.hasta}
        etiquetaRango={etiquetaRango}
        onCerrar={() => setReporteVisible(false)}
      />
      <ModalAjustarInicioRealPU
        visible={!!ordenAjuste}
        ordenId={ordenAjuste?._id}
        folio={ordenAjuste?.folio}
        inicioAjustado={ordenAjuste?.avance?.inicioAjustado}
        inicioRealAutomatico={ordenAjuste?.avance?.inicioRealAutomatico}
        onCerrar={() => setOrdenAjuste(null)}
        onGuardado={() => {
          setOrdenAjuste(null);
          onRefrescar && onRefrescar();
        }}
      />
    </div>
  );
};

export default PlaneacionPresupuestoPU;
